import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { makeStyles, Typography, Grid } from '@material-ui/core';
import CopyToClipboard from 'react-copy-to-clipboard';
import Dotdotdot from 'react-dotdotdot';

import IconLink from '../../assets/images/ic-link.svg';
import { defaultReceieverFields } from '../../blocs/share-link.bloc';
import ShareReportEmail from '../../models/ShareReportEmail';
import useBlocRef from '../../utils/useBlocRef.hook';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';

const useStyles = makeStyles((theme) => ({
  button: {
    marginTop: '8px',
    padding: '6px 34px',
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      marginTop: '0px',
      padding: '11px 34px',
      width: 'auto',
    },
  },
  buttonBlock: {
    width: '100%',
  },
  circular: {
    marginLeft: '8px',
  },
  circularContainer: {
    height: '30px',
  },
  container: {
    padding: '36px',
    [theme.breakpoints.up('lg')]: {
      padding: '30px',
      width: '519px',
    },
  },
  emailField: {
    paddingLeft: '0px',
    [theme.breakpoints.up('md')]: {
      paddingLeft: '10px',
    },
  },
  emailShareContainer: {
    borderTop: '1px solid #F5F5F5',
    padding: '30px 0px',
  },
  fieldContainer: {
    display: 'block',
    [theme.breakpoints.up('lg')]: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },
  },
  fieldContainerWrapper: {
    border: '1px solid #CAD6EA',
    borderRadius: '8px',
    display: 'block',
    padding: '6px',
  },
  fieldContainerWrapperWithMargin: {
    border: '1px solid #CAD6EA',
    borderRadius: '8px',
    display: 'block',
    marginTop: '12px',
    padding: '6px',
  },
  flexContainer: {
    flexFlow: 'nowrap',
  },
  footerContainer: {
    backgroundColor: '#F5F5F5',
    borderRadius: '5px',
    padding: '20px',
  },
  footerDescription: {
    color: '#475C7E',
    fontSize: '12px',
    lineHeight: '18px',
    marinBottom: '0px',
  },
  footerTitle: {
    color: '#172B4D',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '27px',
    marginBottom: '10px',
  },
  headContainer: {
    marginBottom: '20px',
  },
  headSubtitle: {
    color: '#999999',
    fontSize: '14px',
    lineHeight: '21px',
    marginBottom: '0px',
  },
  headTitle: {
    color: '#172B4D',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '24px',
    marginBottom: '5px',
  },
  iconLink: {
    height: '24px',
    width: 'auto',
  },
  iconLinkContainer: {
    lineHeight: '0%',
  },
  manualShareContainer: {
    borderTop: '1px solid #F5F5F5',
    padding: '30px 0px 20px',
  },
  nameField: {
    paddingRight: '0px',
    [theme.breakpoints.up('md')]: {
      paddingRight: '10px',
    },
  },
  noVerticalMargin: {
    marginBottom: '0px !important',
    marginTop: '0px !important',
  },
  sendShareEmail: {
    marginBottom: '28px',
  },
  subtitle: {
    color: '#999999',
    fontSize: '12px',
    lineHeight: '18px',
    marginBottom: '20px',
    textAlign: 'left',
  },
  textLink: {
    color: '#CCD2E3',
    fontSize: '14px',
    lineHeight: '16px',
    marginBottom: '0px',
    marginLeft: '6px',
    marginRight: '10px',
    overflow: 'hidden',
    textAlign: 'left',
  },
  textLinkContainer: {
    maxWidth: 'none',
    [theme.breakpoints.up('lg')]: {
      maxWidth: '350px',
    },
  },
  title: {
    color: '#172B4D',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '24px',
    marginBottom: '5px',
    textAlign: 'left',
  },
}));

export default ({ resourceId, type = 'video-report' }) => {
  const classes = useStyles();
  const blocRef = useBlocRef('shareLinkBloc');
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState('loading...');
  const [childResources, setChildResources] = useState([]);
  const [receiverFields, setReceiverFields] = useState(defaultReceieverFields);
  const [mainResource, setMainResource] = useState({});
  const receiverNameRef = useRef();
  const receiverEmailRef = useRef();

  const handleFieldChange = useCallback((field, value) => {
    blocRef.current.updateReceiverField(field, value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const receiverNameField = useMemo(
    () => [
      {
        className: `custom-height small-text ${classes.noVerticalMargin}`,
        name: 'receiverName',
        onChange: (value) => {
          handleFieldChange('receiverName', value);
        },
        placeholder: 'Name',
        type: 'text',
        variant: 'outlined',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const receiverEmailField = useMemo(
    () => [
      {
        className: `custom-height small-text ${classes.noVerticalMargin}`,
        name: 'receiverEmail',
        onChange: (value) => {
          handleFieldChange('receiverEmail', value);
        },
        placeholder: 'Email Address',
        type: 'text',
        variant: 'outlined',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const loadingSubscription = blocRef.current.loading$.subscribe(setLoading);
    const linkSubscription = blocRef.current.shareLink$.subscribe(setLink);
    const receiverFieldsSub = blocRef.current.receiverFields$.subscribe(setReceiverFields);
    const childResourcesSub = blocRef.current.childResources$.subscribe(setChildResources);
    const mainResourceSub = blocRef.current.mainResource$.subscribe(setMainResource);

    if (type === 'video-report') blocRef.current.getShareVideoReport(resourceId);
    else blocRef.current.getShareLink(resourceId);

    return () => {
      loadingSubscription.unsubscribe();
      linkSubscription.unsubscribe();
      receiverFieldsSub.unsubscribe();
      childResourcesSub.unsubscribe();
      mainResourceSub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyClick = (selectedLink = '') => {
    blocRef.current.copyLinkToClipboard(selectedLink || link);
  };

  const handleShareEmail = () => {
    blocRef.current.sendShareEmail(resourceId);
  };

  const fieldsError = ShareReportEmail.validateCreate({ email: receiverFields.receiverEmail, name: receiverFields.receiverName }, ['email', 'name']);
  const isSendShareEmailButtonEnabled = !fieldsError;

  const renderText = (selectedLink = '') => {
    return (
      <div className={classes.textLinkContainer}>
        <Dotdotdot clamp={2}>
          <Typography className={classes.textLink}>{loading ? 'Retrieving link...' : selectedLink || link}</Typography>
        </Dotdotdot>
      </div>
    );
  };

  if (type !== 'video-report') {
    return (
      <div className={classes.container}>
        <Typography className={classes.title}>Manually Share Link</Typography>
        <Typography className={classes.subtitle}>
          Click the copy button below to save the online link to your files. This can then be shared with anyone you would like to have access.
        </Typography>

        <div className={classes.fieldContainerWrapper}>
          {childResources?.length > 0 ? <p style={{ marginBottom: '4px', marginTop: 0 }}>{(mainResource?.description || 'NO TITLE').toUpperCase()} #1</p> : null}

          <div className={classes.fieldContainer}>
            <Grid container className={classes.flexContainer} alignItems="center">
              <Grid item className={classes.iconLinkContainer}>
                <img className={classes.iconLink} src={IconLink} alt="icon link" />
              </Grid>
              <Grid item style={{ width: '90%' }}>
                {renderText()}
              </Grid>
            </Grid>
            <CopyToClipboard disabled={loading} text={link} onCopy={() => handleCopyClick()}>
              <LoadingButton color="primary" variant="contained" disabled={loading} className={classes.button} txtColor="#FFFFFF">
                Copy
              </LoadingButton>
            </CopyToClipboard>
          </div>
        </div>

        {childResources.map((childResource, index) => {
          return (
            <div className={classes.fieldContainerWrapperWithMargin}>
              <p style={{ marginBottom: '4px', marginTop: 0 }}>
                {(childResource?.description || 'NO TITLE').toUpperCase()} #{index + 2}
              </p>

              <div key={childResource.id} className={classes.fieldContainer}>
                <Grid container className={classes.flexContainer} alignItems="center">
                  <Grid item className={classes.iconLinkContainer}>
                    <img className={classes.iconLink} src={IconLink} alt="icon link" />
                  </Grid>
                  <Grid item style={{ width: '90%' }}>
                    {renderText(childResource.viewerLink)}
                  </Grid>
                </Grid>
                <CopyToClipboard disabled={loading} text={childResource.viewerLink} onCopy={() => handleCopyClick(childResource.viewerLink)}>
                  <LoadingButton color="primary" variant="contained" disabled={loading} className={classes.button} txtColor="#FFFFFF">
                    Copy
                  </LoadingButton>
                </CopyToClipboard>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.headContainer}>
        <Typography className={classes.headTitle}>Share your video report</Typography>
        <Typography className={classes.headSubtitle}>
          If you would like to share your video report with your physician, family or friends, you can use one of the two options below.
        </Typography>
      </div>

      <div className={classes.manualShareContainer}>
        <Typography className={classes.title}>Manually Share Link</Typography>
        <Typography className={classes.subtitle}>
          Click the copy button below to save the online link to your report. This can then be shared with anyone you would like to have access.
        </Typography>

        <div className={classes.fieldContainerWrapper}>
          <div className={classes.fieldContainer}>
            <Grid container className={classes.flexContainer} alignItems="center">
              <Grid item className={classes.iconLinkContainer}>
                <img className={classes.iconLink} src={IconLink} alt="icon link" />
              </Grid>
              <Grid item style={{ width: '90%' }}>
                {renderText()}
              </Grid>
            </Grid>
            <CopyToClipboard disabled={loading} text={link} onCopy={handleCopyClick}>
              <LoadingButton color="primary" variant="contained" disabled={loading} className={classes.button} txtColor="#FFFFFF">
                Copy
              </LoadingButton>
            </CopyToClipboard>
          </div>
        </div>
      </div>

      <div className={classes.emailShareContainer}>
        <Typography className={classes.title}>Send Share Email</Typography>
        <Typography className={classes.subtitle}>
          Enter the name and email address of someone you would like to share your video report with and we will email them the link.
        </Typography>

        {/* Fields */}
        <Grid container>
          <Grid item xs={12} className={`${classes.sendShareEmail} ${classes.nameField}`} md={5}>
            <EditFields useLabel={false} labelPosition="top" ref={receiverNameRef} fields={receiverNameField} />
          </Grid>
          <Grid item xs={12} className={`${classes.sendShareEmail} ${classes.emailField}`} md={7}>
            <EditFields useLabel={false} labelPosition="top" ref={receiverEmailRef} fields={receiverEmailField} />
          </Grid>
        </Grid>

        <LoadingButton
          color="primary"
          variant="contained"
          loading$={blocRef.current.loadingSendShareEmail$}
          disabled={!isSendShareEmailButtonEnabled}
          className={`${classes.button} ${classes.buttonBlock} ${classes.noVerticalMargin}`}
          onClick={handleShareEmail}>
          Send Share Email
        </LoadingButton>
      </div>

      <div className={classes.footerContainer}>
        <Typography className={classes.footerTitle}>Take Note:</Typography>
        <Typography className={classes.footerDescription}>
          Sharing your video report using either method above will only give the recipient access to this specific video report, they will not have access to educational materials,
          previously created reports or our online Mediphany portal.
        </Typography>
      </div>
    </div>
  );
};
